import dayjs from 'dayjs';
import {
  Box,
  BoxProps,
  ElementProps,
  factory,
  Factory,
  StylesApiProps,
  useProps,
} from '@mantine/core';
import { DateLabelFormat, DateStringValue } from '../../types';
import {
  CalendarHeader,
  CalendarHeaderSettings,
  CalendarHeaderStylesNames,
} from '../CalendarHeader';
import { useDatesContext } from '../DatesProvider';
import { MonthsList, MonthsListSettings, MonthsListStylesNames } from '../MonthsList';

export type YearLevelStylesNames = MonthsListStylesNames | CalendarHeaderStylesNames;

export interface YearLevelBaseSettings extends MonthsListSettings {
  /** dayjs label format to display year label or a function that returns year label based on year value @default "YYYY" */
  yearLabelFormat?: DateLabelFormat;
}

export interface YearLevelSettings extends YearLevelBaseSettings, CalendarHeaderSettings {}

export interface YearLevelProps
  extends
    BoxProps,
    YearLevelSettings,
    Omit<StylesApiProps<YearLevelFactory>, 'classNames' | 'styles'>,
    ElementProps<'div'> {
  classNames?: Partial<Record<string, string>>;
  styles?: Partial<Record<string, React.CSSProperties>>;
  __staticSelector?: string;

  /** Displayed year value in `YYYY-MM-DD` format */
  year: DateStringValue;

  /** `aria-label` for change level control */
  levelControlAriaLabel?: string;

  /** Determines whether the calendar should take the full width of its container @default false */
  fullWidth?: boolean;

  /** Called when date is changed via native level select */
  __onDateChange?: (date: DateStringValue) => void;

  /** Disables native level selects when date changes would not take effect */
  __disableNativeLevelSelect?: boolean;
}

export type YearLevelFactory = Factory<{
  props: YearLevelProps;
  ref: HTMLDivElement;
  stylesNames: YearLevelStylesNames;
}>;

const defaultProps = {
  yearLabelFormat: 'YYYY',
} satisfies Partial<YearLevelProps>;

export const YearLevel = factory<YearLevelFactory>((_props) => {
  const props = useProps('YearLevel', defaultProps, _props);
  const {
    // MonthsList settings
    year,
    locale,
    minDate,
    maxDate,
    monthsListFormat,
    getMonthControlProps,
    __getControlRef,
    __onControlKeyDown,
    __onControlClick,
    __onControlMouseEnter,
    withCellSpacing,

    // CalendarHeader settings
    __preventFocus,
    nextIcon,
    previousIcon,
    nextLabel,
    previousLabel,
    onNext,
    onPrevious,
    onLevelClick,
    nextDisabled,
    previousDisabled,
    hasNextLevel,
    levelControlAriaLabel,
    withNext,
    withPrevious,
    headerControlsOrder,
    withNativeLevelSelect,
    yearsSelectRange,

    // Other props
    yearLabelFormat,
    __staticSelector,
    __stopPropagation,
    __onDateChange,
    __disableNativeLevelSelect,
    size,
    classNames,
    styles,
    unstyled,
    fullWidth,
    attributes,
    ...others
  } = props;

  const ctx = useDatesContext();

  const stylesApiProps = {
    __staticSelector: __staticSelector || 'YearLevel',
    classNames,
    styles,
    unstyled,
    size,
    attributes,
  };

  const _nextDisabled =
    typeof nextDisabled === 'boolean'
      ? nextDisabled
      : maxDate
        ? !dayjs(year).endOf('year').isBefore(maxDate)
        : false;

  const _previousDisabled =
    typeof previousDisabled === 'boolean'
      ? previousDisabled
      : minDate
        ? !dayjs(year).startOf('year').isAfter(minDate)
        : false;

  return (
    <Box data-year-level size={size} {...others}>
      <CalendarHeader
        label={
          typeof yearLabelFormat === 'function'
            ? yearLabelFormat(year)
            : dayjs(year)
                .locale(locale || ctx.locale)
                .format(yearLabelFormat)
        }
        __preventFocus={__preventFocus}
        __stopPropagation={__stopPropagation}
        __calendarLevel="year"
        __date={year}
        __locale={locale || ctx.locale}
        __onDateChange={__onDateChange}
        __disableNativeLevelSelect={__disableNativeLevelSelect}
        __minDate={minDate}
        __maxDate={maxDate}
        nextIcon={nextIcon}
        previousIcon={previousIcon}
        nextLabel={nextLabel}
        previousLabel={previousLabel}
        onNext={onNext}
        onPrevious={onPrevious}
        onLevelClick={onLevelClick}
        nextDisabled={_nextDisabled}
        previousDisabled={_previousDisabled}
        hasNextLevel={hasNextLevel}
        levelControlAriaLabel={levelControlAriaLabel}
        withNext={withNext}
        withPrevious={withPrevious}
        headerControlsOrder={headerControlsOrder}
        withNativeLevelSelect={withNativeLevelSelect}
        yearsSelectRange={yearsSelectRange}
        fullWidth={fullWidth}
        {...stylesApiProps}
      />

      <MonthsList
        year={year}
        locale={locale}
        minDate={minDate}
        maxDate={maxDate}
        monthsListFormat={monthsListFormat}
        getMonthControlProps={getMonthControlProps}
        __getControlRef={__getControlRef}
        __onControlKeyDown={__onControlKeyDown}
        __onControlClick={__onControlClick}
        __onControlMouseEnter={__onControlMouseEnter}
        __preventFocus={__preventFocus}
        __stopPropagation={__stopPropagation}
        withCellSpacing={withCellSpacing}
        fullWidth={fullWidth}
        {...stylesApiProps}
      />
    </Box>
  );
});

YearLevel.classes = { ...CalendarHeader.classes, ...MonthsList.classes };
YearLevel.displayName = '@mantine/dates/YearLevel';
